"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, BellOff, Apple } from "lucide-react";
import { api, loadTokens } from "@/lib/api";

/**
 * EPIC-048 STORY-2004 — browser-level web-push subscription toggle.
 *
 * Shown above the per-category preferences table. Three states drive
 * the rendered surface:
 *
 *   1. "ios-needs-install" — Safari on iOS where the page is NOT
 *      running in standalone mode. iOS doesn't allow
 *      `Notification.requestPermission()` on a non-installed PWA;
 *      we render install instructions instead of a useless button.
 *   2. "unsupported" — browser without ServiceWorker / PushManager /
 *      Notification API. Hidden, no surface.
 *   3. "ready" — every other case. Shows the enable / disable toggle.
 *
 * Permission timing: the prompt is deferred until the user explicitly
 * clicks Enable. We never auto-prompt on settings-page mount.
 */

type State =
  | "loading"
  | "ios-needs-install"
  | "unsupported"
  | "no-public-key"
  | "permission-denied"
  | "subscribed"
  | "not-subscribed";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufferToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++)
    bin += String.fromCharCode(bytes[i]);
  return window
    .btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function detectIosNeedsInstall(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIos = /iP(hone|ad|od)/.test(ua);
  if (!isIos) return false;
  // Safari iOS allows push only when the page is running in standalone
  // (i.e. installed to home screen). `navigator.standalone` is the iOS
  // signal; matchMedia('display-mode: standalone') covers other UAs.
  const standalone = (window.navigator as { standalone?: boolean }).standalone;
  if (standalone === true) return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return false;
  return true;
}

function isPushSupported(): boolean {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function WebPushToggle() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>("loading");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (detectIosNeedsInstall()) {
        if (!cancelled) setState("ios-needs-install");
        return;
      }
      if (!isPushSupported()) {
        if (!cancelled) setState("unsupported");
        return;
      }
      const key = await api.getPushPublicKey();
      if (!key) {
        if (!cancelled) setState("no-public-key");
        return;
      }
      const permission = window.Notification.permission;
      if (permission === "denied") {
        if (!cancelled) setState("permission-denied");
        return;
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const existing = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancelled) setState(existing ? "subscribed" : "not-subscribed");
      } catch {
        if (!cancelled) setState("not-subscribed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    if (busy) return;
    if (!loadTokens()) {
      setError(t("webPushToggle.errorSignedOut"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const key = await api.getPushPublicKey();
      if (!key) {
        setState("no-public-key");
        return;
      }
      const perm = await window.Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "permission-denied" : "not-subscribed");
        return;
      }
      // Most apps register a service worker globally; until STORY-2001
      // ships ours we register a minimal stub so pushManager.subscribe
      // has something to attach to. The stub forwards push events to a
      // notification — when STORY-2001 lands, that file replaces this
      // one in-place at the same scope.
      const reg = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });
      const appKey = urlBase64ToUint8Array(key.publicKey);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // Cast required: TS lib.dom defines applicationServerKey as
        // BufferSource backed by an ArrayBuffer, but Uint8Array's
        // ArrayBufferLike includes SharedArrayBuffer in its union.
        applicationServerKey: appKey as unknown as ArrayBuffer,
      });
      const json = sub.toJSON() as {
        endpoint?: string;
        keys?: { p256dh?: string; auth?: string };
      };
      const endpoint =
        json.endpoint ?? bufferToBase64Url(sub.getKey("p256dh"));
      const p256dhKey =
        json.keys?.p256dh ?? bufferToBase64Url(sub.getKey("p256dh"));
      const authKey = json.keys?.auth ?? bufferToBase64Url(sub.getKey("auth"));
      await api.subscribePush({
        endpoint,
        p256dhKey,
        authKey,
        userAgent: window.navigator.userAgent.slice(0, 512),
      });
      setState("subscribed");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("webPushToggle.errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const endpoint = sub.endpoint;
        // Server-side delete first; if user is offline, the client
        // unsubscribe still happens so the browser stops receiving
        // pushes — server-side prune-stale catches the orphan later.
        try {
          await api.unsubscribePush(endpoint);
        } catch {
          // best-effort; client unsub still proceeds
        }
        await sub.unsubscribe();
      }
      setState("not-subscribed");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("webPushToggle.errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  if (state === "loading" || state === "unsupported") {
    return null;
  }

  return (
    <div
      className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
      role="region"
      aria-label={t("webPushToggle.aria")}
    >
      {state === "ios-needs-install" && (
        <div className="flex items-start gap-3">
          <Apple
            size={18}
            strokeWidth={2}
            aria-hidden="true"
            style={{ color: "var(--color-text2)" }}
          />
          <div className="flex-1 text-[13px]">
            <strong className="block text-[var(--color-text)]">
              {t("webPushToggle.iosTitle")}
            </strong>
            <p className="mt-1 text-[var(--color-text3)]">
              {t("webPushToggle.iosBody")}
            </p>
            <ol className="mt-2 list-decimal pl-5 text-[12px] text-[var(--color-text3)]">
              <li>{t("webPushToggle.iosStep1")}</li>
              <li>{t("webPushToggle.iosStep2")}</li>
              <li>{t("webPushToggle.iosStep3")}</li>
            </ol>
          </div>
        </div>
      )}

      {state === "no-public-key" && (
        <div className="flex items-start gap-3">
          <BellOff
            size={18}
            strokeWidth={2}
            aria-hidden="true"
            style={{ color: "var(--color-text3)" }}
          />
          <div className="flex-1 text-[13px]">
            <strong className="block text-[var(--color-text)]">
              {t("webPushToggle.unconfiguredTitle")}
            </strong>
            <p className="mt-1 text-[var(--color-text3)]">
              {t("webPushToggle.unconfiguredBody")}
            </p>
          </div>
        </div>
      )}

      {state === "permission-denied" && (
        <div className="flex items-start gap-3">
          <BellOff
            size={18}
            strokeWidth={2}
            aria-hidden="true"
            style={{ color: "var(--color-no)" }}
          />
          <div className="flex-1 text-[13px]">
            <strong className="block text-[var(--color-text)]">
              {t("webPushToggle.deniedTitle")}
            </strong>
            <p className="mt-1 text-[var(--color-text3)]">
              {t("webPushToggle.deniedBody")}
            </p>
          </div>
        </div>
      )}

      {(state === "subscribed" || state === "not-subscribed") && (
        <div className="flex items-center gap-3">
          {state === "subscribed" ? (
            <Bell
              size={18}
              strokeWidth={2}
              aria-hidden="true"
              style={{ color: "var(--color-brand)" }}
            />
          ) : (
            <BellOff
              size={18}
              strokeWidth={2}
              aria-hidden="true"
              style={{ color: "var(--color-text3)" }}
            />
          )}
          <div className="flex-1 text-[13px]">
            <strong className="block text-[var(--color-text)]">
              {t("webPushToggle.title")}
            </strong>
            <p className="mt-0.5 text-[12px] text-[var(--color-text3)]">
              {state === "subscribed"
                ? t("webPushToggle.bodyOn")
                : t("webPushToggle.bodyOff")}
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={state === "subscribed" ? disable : enable}
            className="rounded-md border px-3 py-1.5 text-[12px] font-bold uppercase tracking-[0.5px] disabled:opacity-50"
            style={{
              borderColor:
                state === "subscribed"
                  ? "var(--color-border)"
                  : "var(--color-brand)",
              background:
                state === "subscribed"
                  ? "var(--color-bg)"
                  : "var(--color-brand)",
              color:
                state === "subscribed" ? "var(--color-text)" : "white",
            }}
          >
            {state === "subscribed"
              ? t("webPushToggle.disable")
              : t("webPushToggle.enable")}
          </button>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="mt-3 text-[12px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
